import React, { ChangeEvent, useRef, useState } from 'react';
import {
  Dealer,
  Filter,
  FilterItem,
  IDealerLocatorLabels,
} from 'interfaces/dealerLocator';
import { Card, Checkbox, FormControl, Select } from '@mui/material';
import { Icon, Icons } from 'components/Global/Icon/Icon';
import {
  useStyles,
  CustomInput,
  CustomMenuItem,
} from './FilterDropdown.styles';
import { appPalette } from 'theme/App.palette';
import TextRender from 'components/Global/TextRender/TextRender';
import { trackEvent } from 'helpers/Analytics';

export interface IFilterDropdown {
  items: FilterItem[];
  changeFunction: (select: any) => void;
  disabled?: boolean;
  selectId?: string;
  nameModifier?: string;
  labels: IDealerLocatorLabels;
}

function getFilterItems(
  filterList: Array<FilterItem>,
  preSelectedFilters: string[]
): Array<FilterItem> {
  return filterList.filter(
    (filter) =>
      filter.show ||
      filter.codes.find((code) => preSelectedFilters.includes(code.code))
  );
}

function getDealerAttributes(dealerList: Dealer[] | undefined): Set<string> {
  const attributes = new Set<string>();
  if (dealerList) {
    dealerList.forEach((dealer) => {
      dealer.attributes.forEach((attribute) => attributes.add(attribute.code));
    });
  }
  return attributes;
}

function addShowAttributeToFilterItems(
  filters: Filter[],
  dealerAttributes: Set<string>
) {
  filters.forEach((filter) => {
    filter.items.forEach((item) => {
      const codes = item.codes.map((code) => code.code);
      if (codes.filter((code) => dealerAttributes.has(code)).length > 0) {
        item.show = true;
      } else {
        item.show = false;
      }
    });
  });
  return filters;
}

export function getVisibleFilterItems(
  dealerLocatorFilters: Filter[],
  dealerList: Dealer[] | undefined,
  preSelectedFilters: string[]
): Array<FilterItem> {
  const listOfDealerAttributes = getDealerAttributes(dealerList);
  let visibleFilters = dealerLocatorFilters.filter(
    (filter) => !filter.isHidden
  );
  visibleFilters = addShowAttributeToFilterItems(
    visibleFilters,
    listOfDealerAttributes
  );
  const filterItems: Array<FilterItem> = new Array<FilterItem>();
  visibleFilters.forEach((visibleFilter) => {
    filterItems.push(
      ...getFilterItems(visibleFilter.items as FilterItem[], preSelectedFilters)
    );
  });

  return filterItems;
}

function getFilterRenderValue(
  labels: IDealerLocatorLabels,
  filterItems?: FilterItem[]
): JSX.Element {
  const haveFilterItems = filterItems && filterItems.length > 0;
  const filterTitle = haveFilterItems
    ? labels.filterTitleBy?.toUpperCase()
    : labels.filterTitle?.toUpperCase();
  const filterValue =
    filterItems?.length === 1
      ? filterItems[0].title
      : `${filterItems?.length || 1} ${labels.filterTitleSelected}`;
  return (
    <>
      <span className="acr-caption-9">
        <TextRender text={filterTitle} />
      </span>{' '}
      {haveFilterItems && (
        <span className="acr-paragraph-5">
          <TextRender text={filterValue} />
        </span>
      )}
    </>
  );
}

export function FilterDropdown({
  labels,
  items,
  disabled,
  selectId,
  changeFunction,
}: IFilterDropdown): JSX.Element {
  const classes = useStyles();
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [filterTooltipContent, setFilterTooltipContent] =
    useState<FilterItem | undefined>();
  const menuRef: any = useRef();
  const inputRef: any = useRef();
  const colors = appPalette.colors;

  const toggleSelectOpen = (e: ChangeEvent<{}>) => {
    if (isSelectOpen && filterTooltipContent) {
      closeFilterTooltip(e);
    }
    setIsSelectOpen(!isSelectOpen);
  };

  const openFilterTooltip = (e: ChangeEvent<{}>, filterItem: FilterItem) => {
    e?.stopPropagation();

    // Set a time out to render the dropdown after the toltip and set the properly height
    setTimeout(() => {
      setFilterTooltipContent(filterItem);
    }, 100);

    trackEvent({
      label: `selector:${filterItem.title}:info icon`,
      category: {
        includePageName: true,
        text: 'selector',
      },
      type: 'selector',
    });
  };

  const closeFilterTooltip = (e: ChangeEvent<{}>) => {
    setFilterTooltipContent(undefined);
  };

  let selectedItemsNames: Array<string> = [];
  if (items && items.length > 0) {
    selectedItemsNames = items.reduce(function (
      result: string[],
      item: FilterItem
    ) {
      if (item.isSelected) {
        result.push(item.title);
      }
      return result;
    },
    []);

    return (
      <FormControl
        className={`${classes.formControl} filter-dropdown`}
        disabled={disabled}
      >
        <Select
          labelId="dropdown-select"
          id={selectId}
          multiple
          displayEmpty={true}
          value={selectedItemsNames}
          onChange={changeFunction}
          onOpen={toggleSelectOpen}
          onClose={toggleSelectOpen}
          ref={inputRef}
          className={`${classes.filterDropdown} ${
            isSelectOpen ? 'select-open' : ''
          }`}
          input={<CustomInput />}
          inputProps={{
            classes: { icon: classes.hidden },
          }}
          MenuProps={{
            classes: {
              paper: classes.selectMenu,
              list: `${classes.selectList} paragraph-6`,
            },
            MenuListProps: {
              ref: menuRef,
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'center',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
          }}
          renderValue={() => {
            return getFilterRenderValue(
              labels,
              items?.filter((item) => item.isSelected)
            );
          }}
        >
          {items &&
            items.map((filterItem: FilterItem, index: number) => (
              <CustomMenuItem
                className={classes.listItem}
                key={filterItem.title}
                value={filterItem.title}
                onKeyUp={(e: any) => {
                  openFilterTooltip(e, filterItem);
                }}
              >
                <Checkbox
                  className={classes.checkbox}
                  icon={<span className={classes.checkboxIcon} />}
                  checkedIcon={<span className={classes.checkboxIconChecked} />}
                  checked={filterItem.isSelected || false}
                />
                <span className={'acr-paragraph-5'}>
                  <TextRender text={filterItem.title} tooltipBlurSafe />
                </span>
                <button
                  onClick={(e) => {
                    openFilterTooltip(e, filterItem);
                  }}
                  className={`${classes.filterTooltip} acr-paragraph-10 filter-tooltip`}
                >
                  i
                </button>
              </CustomMenuItem>
            ))}
        </Select>
        {filterTooltipContent && (
          <Card
            style={{
              top:
                menuRef.current?.getBoundingClientRect().height +
                inputRef.current?.getBoundingClientRect().height,
            }}
            variant="outlined"
            className={`${classes.filterCard} filter-card`}
          >
            <button
              aria-label="Close Modal"
              className={`st-icon-button-focus ${classes.close_button}`}
              onClick={closeFilterTooltip}
            >
              <Icon type={Icons.close} color={colors.mid_grey} />
            </button>
            <h5 className="acr-caption-6">
              <TextRender text={filterTooltipContent.title} tooltipBlurSafe />
            </h5>
            <p className="acr-paragraph-5">
              <TextRender
                text={filterTooltipContent.description}
                tooltipBlurSafe
              />
            </p>
          </Card>
        )}
      </FormControl>
    );
  } else {
    return <></>;
  }
}
